import { z } from 'zod'
import { CODICI_CONTATORE, type CodiceContatore } from '@praticheenergia/domain'
import { supabase } from '../lib/supabase'

/** Stringa vuota = campo non compilato: nel database e' `null`, non ''. */
const opzionale = (formato: RegExp, messaggio: string) =>
  z
    .string()
    .trim()
    .transform((v) => (v === '' ? null : v))
    .refine((v) => v === null || formato.test(v), messaggio)

// Stessi vincoli dei check di 0014_contatore_pod.sql e 0030_motore_mensile.sql:
// l'errore va detto nel dialogo, non restituito da Postgres.
export const SchemaContatore = z.object({
  codice: z.enum(CODICI_CONTATORE),
  matricola: z.string().trim().min(1, 'Matricola obbligatoria'),
  cifreIntere: z.number().int().min(0, 'Cifre intere non valide'),
  /** Stringa fino al database, mai number: moltiplica ogni lettura. */
  costanteLettura: z
    .string()
    .trim()
    .transform((v) => v.replace(',', '.'))
    .refine((v) => /^\d+(\.\d+)?$/.test(v) && Number(v) > 0, 'Costante di lettura non valida'),
  kwhDiretti: z.boolean(),
  tipoScambio: opzionale(/^[A-KRS]$/, 'Tipo di scambio non valido'),
  idControparte: opzionale(/^.+$/, ''),
  tipologiaConsumo: opzionale(/^(J[1-7]|L[1-9]|M([1-9]|1[0-2])|M4\+M5\+M6\+M7)$/, 'Tipologia di consumo non valida'),
})

export type DatiContatore = z.input<typeof SchemaContatore>

export interface Contatore {
  readonly id: number
  readonly codice: CodiceContatore
  readonly matricola: string
  readonly cifreIntere: number
  readonly costanteLettura: string
  readonly kwhDiretti: boolean
  readonly tipoScambio: string | null
  readonly idControparte: string | null
  readonly tipologiaConsumo: string | null
}

interface RigaContatore {
  id: number
  codice: CodiceContatore
  matricola: string
  cifre_intere: number
  costante_lettura: number | string
  kwh_diretti: boolean
  tipo_scambio: string | null
  id_controparte: string | null
  tipologia_consumo: string | null
}

export async function elencaContatori(impiantoId: number): Promise<Contatore[]> {
  const { data, error } = await supabase
    .from('contatore')
    .select('id, codice, matricola, cifre_intere, costante_lettura, kwh_diretti, tipo_scambio, id_controparte, tipologia_consumo')
    .eq('impianto_id', impiantoId)
    .order('id')
  if (error) throw new Error(error.message)
  return ((data ?? []) as RigaContatore[]).map((r) => ({
    id: r.id,
    codice: r.codice,
    matricola: r.matricola,
    cifreIntere: r.cifre_intere,
    costanteLettura: String(r.costante_lettura),
    kwhDiretti: r.kwh_diretti,
    tipoScambio: r.tipo_scambio,
    idControparte: r.id_controparte,
    tipologiaConsumo: r.tipologia_consumo,
  }))
}

function colonne(dati: DatiContatore) {
  const d = SchemaContatore.parse(dati)
  return {
    codice: d.codice,
    matricola: d.matricola,
    cifre_intere: d.cifreIntere,
    costante_lettura: d.costanteLettura,
    kwh_diretti: d.kwhDiretti,
    tipo_scambio: d.tipoScambio,
    id_controparte: d.idControparte,
    tipologia_consumo: d.tipologiaConsumo,
  }
}

function errore(e: { code?: string; message: string }): Error {
  // contatore_matricola_idx: una matricola per impianto.
  return new Error(e.code === '23505' ? 'Matricola gia presente su questo impianto' : e.message)
}

export async function creaContatore(impiantoId: number, dati: DatiContatore): Promise<void> {
  const riga = { impianto_id: impiantoId, ...colonne(dati) }
  const { error } = await supabase.from('contatore').insert(riga)
  if (error) throw errore(error)
}

export async function aggiornaContatore(id: number, dati: DatiContatore): Promise<void> {
  const { error } = await supabase.from('contatore').update(colonne(dati)).eq('id', id)
  if (error) throw errore(error)
}

export async function eliminaContatore(id: number): Promise<void> {
  const { error } = await supabase.from('contatore').delete().eq('id', id)
  if (error) throw errore(error)
}
